'use client'

import { colors } from '@/components/atoms/colors'
import RectangleButton from '@/components/molecules/rectangleButton'
import Spacer from '@/components/atoms/spacer'
import ResponsiveTitle from '@/components/molecules/responsiveTitle'
import Container from '@/components/templates/container'
import Layout from '@/components/templates/layout'
import { useMediaQuery } from '@mui/material'
import { useEffect, useRef } from 'react'

const PAD_WIDTH = 5
const PAD_HEIGHT = 130
const PAD_MARGIN_X = 50
const PAD_SPEED = 520 // px/sec
const BALL_RADIUS = 12.5
const BALL_SPEED = 640 // px/sec (base)
const BALL_MAX_SPEED = 980
const SPIN = 420 // how much vertical speed added from impact position
const EPS = 0.5 // nudge to push ball out of paddle to avoid “flip-flop”
const MIN_DESKTOP = 768

function NotFoundPage({ footerAPI, notFoundListAPI, settingAPI }) {
  const isMobile = useMediaQuery('(max-width:767px)')
  const containerRef = useRef(null)
  const canvasRef = useRef(null)

  // All game state lives here; never causes React re-renders.
  const gs = useRef({
    ctx: null,
    dpr: 1,
    w: 0,
    h: 0,
    running: false,
    idle: true,
    raf: 0,
    lastT: 0,
    keys: { w: false, s: false, ArrowUp: false, ArrowDown: false },
    pad1: { x: PAD_MARGIN_X, y: 0 },
    pad2: { x: 0, y: 0 },
    ball: { x: 0, y: 0, vx: 0, vy: 0, r: BALL_RADIUS },
    resizeObs: null,
    pointers: {}, // { [pointerId]: 'left' | 'right' }
  })

  // ---- Helpers ----
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v))
  const circleRectHit = (cx, cy, cr, rx, ry, rw, rh) => {
    // Closest point on rect to circle center
    const nx = clamp(cx, rx, rx + rw)
    const ny = clamp(cy, ry, ry + rh)
    const dx = cx - nx
    const dy = cy - ny
    return dx * dx + dy * dy <= cr * cr
  }

  const drawStatic = () => {
    const s = gs.current
    const { ctx, w, h, pad1, pad2, ball } = s
    ctx.clearRect(0, 0, w, h)

    // Mid dashed line
    ctx.fillStyle = colors.umber

    // Paddles
    ctx.fillStyle = colors.umber
    ctx.fillRect(
      pad1.x - PAD_WIDTH / 2,
      pad1.y - PAD_HEIGHT / 2,
      PAD_WIDTH,
      PAD_HEIGHT,
    )
    ctx.fillRect(
      pad2.x - PAD_WIDTH / 2,
      pad2.y - PAD_HEIGHT / 2,
      PAD_WIDTH,
      PAD_HEIGHT,
    )

    // Ball (always visible in idle)
    ctx.beginPath()
    ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2)
    ctx.fillStyle = colors.khaki
    ctx.fill()
  }

  const resetPositions = () => {
    const s = gs.current
    s.pad1.y = s.h / 2
    s.pad2.y = s.h / 2
    s.pad2.x = s.w - PAD_MARGIN_X
    s.ball.x = s.w / 2
    s.ball.y = s.h / 2
    s.ball.vx = 0
    s.ball.vy = 0
    s.idle = true
    s.running = false
    s.lastT = 0
    s.pointers = {}
    drawStatic()
  }

  const startPlay = () => {
    const s = gs.current
    if (s.running) return
    // Launch ball with random direction
    const dirX = Math.random() < 0.5 ? -1 : 1
    const dirY = (Math.random() * 2 - 1) * 0.6 // a bit of vertical variance
    const mag = Math.sqrt(dirX * dirX + dirY * dirY) || 1
    s.ball.vx = (dirX / mag) * BALL_SPEED
    s.ball.vy = (dirY / mag) * BALL_SPEED
    s.idle = false
    s.running = true
    s.lastT = 0
    if (!s.raf) s.raf = requestAnimationFrame(loop)
  }

  const stopLoop = () => {
    const s = gs.current
    if (s.raf) cancelAnimationFrame(s.raf)
    s.raf = 0
  }

  const handleKeys = (dt) => {
    const s = gs.current
    const dy1 = (s.keys.w ? -1 : 0) + (s.keys.s ? 1 : 0)
    const dy2 = (s.keys.ArrowUp ? -1 : 0) + (s.keys.ArrowDown ? 1 : 0)
    if (dy1)
      s.pad1.y = clamp(
        s.pad1.y + dy1 * PAD_SPEED * dt,
        PAD_HEIGHT / 2,
        s.h - PAD_HEIGHT / 2,
      )
    if (dy2)
      s.pad2.y = clamp(
        s.pad2.y + dy2 * PAD_SPEED * dt,
        PAD_HEIGHT / 2,
        s.h - PAD_HEIGHT / 2,
      )
  }

  // Prevent the classic “flip-flop” jitter: resolve collisions once per frame,
  // reflect velocity, then push ball out by EPS so next frame doesn’t detect the same hit.
  const resolveCollisions = () => {
    const s = gs.current
    const { w, h, ball } = s

    // Walls
    if (ball.y + ball.r > h) {
      ball.y = h - ball.r - EPS
      ball.vy = -Math.abs(ball.vy)
    } else if (ball.y - ball.r < 0) {
      ball.y = ball.r + EPS
      ball.vy = Math.abs(ball.vy)
    }

    // Left paddle
    const p1 = {
      x: s.pad1.x - PAD_WIDTH / 2,
      y: s.pad1.y - PAD_HEIGHT / 2,
      w: PAD_WIDTH,
      h: PAD_HEIGHT,
    }
    if (
      circleRectHit(ball.x, ball.y, ball.r, p1.x, p1.y, p1.w, p1.h) &&
      ball.vx < 0
    ) {
      // Add spin based on hit position
      const rel = (ball.y - (p1.y + PAD_HEIGHT / 2)) / (PAD_HEIGHT / 2) // -1..1
      ball.vx = Math.abs(ball.vx)
      ball.vy += rel * SPIN
      // Push out
      ball.x = p1.x + p1.w + ball.r + EPS
    }

    // Right paddle
    const p2 = {
      x: s.pad2.x - PAD_WIDTH / 2,
      y: s.pad2.y - PAD_HEIGHT / 2,
      w: PAD_WIDTH,
      h: PAD_HEIGHT,
    }
    if (
      circleRectHit(ball.x, ball.y, ball.r, p2.x, p2.y, p2.w, p2.h) &&
      ball.vx > 0
    ) {
      const rel = (ball.y - (p2.y + PAD_HEIGHT / 2)) / (PAD_HEIGHT / 2)
      ball.vx = -Math.abs(ball.vx)
      ball.vy += rel * SPIN
      ball.x = p2.x - ball.r - EPS
    }

    // Cap speed to avoid tunneling on high FPS or tiny paddles
    const sp = Math.hypot(ball.vx, ball.vy)
    if (sp > BALL_MAX_SPEED) {
      const f = BALL_MAX_SPEED / sp
      ball.vx *= f
      ball.vy *= f
    }

    // Score (out of bounds): reset to idle, keep ball shown center
    if (ball.x - ball.r > w || ball.x + ball.r < 0) {
      resetPositions()
    }
  }

  const loop = (t) => {
    const s = gs.current
    s.raf = requestAnimationFrame(loop)

    // timestamp-based delta
    if (!s.lastT) s.lastT = t
    let dt = (t - s.lastT) / 1000
    s.lastT = t
    // clamp dt to avoid huge jumps when tab regains focus
    dt = Math.min(dt, 1 / 30)

    // If tab hidden or idle, just redraw static and bail.
    if (document.hidden || !s.running) {
      drawStatic()
      return
    }

    handleKeys(dt)

    // Integrate ball
    s.ball.x += s.ball.vx * dt
    s.ball.y += s.ball.vy * dt

    resolveCollisions()
    drawStatic()
  }

  const getCanvasPos = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  useEffect(() => {
    // Only run game on desktop; mobile shows copy + button only
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    if (window.innerWidth < MIN_DESKTOP && !isTouch) return

    const canvas = canvasRef.current
    canvas.style.touchAction = 'none'
    const container = containerRef.current
    if (!canvas || !container) return

    const s = gs.current
    s.ctx = canvas.getContext('2d')
    s.dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3))

    // DPR-aware sizing with ResizeObserver (smoother than window resize listener)
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect
      s.w = Math.floor(cr.width)
      s.h = Math.floor(cr.height)

      canvas.width = Math.floor(s.w * s.dpr)
      canvas.height = Math.floor(s.h * s.dpr)
      canvas.style.width = `${s.w}px`
      canvas.style.height = `${s.h}px`

      const ctx = s.ctx
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(s.dpr, s.dpr)

      // Set paddle X (depends on width) and reset to center on first layout or when size changes
      s.pad1.x = PAD_MARGIN_X
      s.pad2.x = s.w - PAD_MARGIN_X
      if (s.idle || !s.running) {
        resetPositions()
      } else {
        drawStatic()
      }
    })
    ro.observe(container)
    s.resizeObs = ro

    const onKeyDown = (e) => {
      // prevent page scrolling on controls
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'Space')
        e.preventDefault()
      if (e.code === 'Space') {
        startPlay()
        return
      }
      if (e.key in s.keys) s.keys[e.key] = true
    }
    const onKeyUp = (e) => {
      if (e.key in s.keys) s.keys[e.key] = false
    }

    const onClickCanvas = () => startPlay()
    const onVis = () => {
      // Pause integration when hidden, resume timestamps on show
      if (!document.hidden) s.lastT = 0
    }

    window.addEventListener('keydown', onKeyDown, { passive: false })
    window.addEventListener('keyup', onKeyUp)
    document.addEventListener('visibilitychange', onVis)
    canvas.addEventListener('click', onClickCanvas)

    // Start RAF (idle render so paddles can move)
    if (!s.raf) s.raf = requestAnimationFrame(loop)
    resetPositions()

    const movePadTo = (side, y) => {
      const s = gs.current
      if (s.idle) return
      const clamped = clamp(y, PAD_HEIGHT / 2, s.h - PAD_HEIGHT / 2)
      if (side === 'left') s.pad1.y = clamped
      else s.pad2.y = clamped
    }

    // start: assign pointer ke sisi, gerakkan paddle, start game jika idle
    // Pointer handlers
    const onPointerDown = (e) => {
      const s = gs.current
      const { x, y } = getCanvasPos(canvas, e.clientX, e.clientY)
      const side = x < s.w / 2 ? 'left' : 'right'

      if (s.idle) {
        // idle: tap untuk START, jangan geser paddle dulu
        s.pointers[e.pointerId] = side
        startPlay()
        canvas.setPointerCapture?.(e.pointerId)
        return
      }

      // saat sudah berjalan: boleh langsung geser
      s.pointers[e.pointerId] = side
      movePadTo(side, y)
      canvas.setPointerCapture?.(e.pointerId)
    }

    const onPointerMove = (e) => {
      const s = gs.current
      if (s.idle) return
      const side = s.pointers[e.pointerId]
      if (!side) return
      const { y } = getCanvasPos(canvas, e.clientX, e.clientY)
      movePadTo(side, y)
    }

    const onPointerUpOrCancel = (e) => {
      const s = gs.current
      delete s.pointers[e.pointerId]
      try {
        canvas.releasePointerCapture?.(e.pointerId)
      } catch {}
    }

    // Detect touch device
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0

    if (isTouchDevice) {
      canvas.addEventListener('pointerdown', onPointerDown)
      canvas.addEventListener('pointermove', onPointerMove)
      canvas.addEventListener('pointerup', onPointerUpOrCancel)
      canvas.addEventListener('pointercancel', onPointerUpOrCancel)
    }

    return () => {
      stopLoop()
      if (isTouchDevice) {
        canvas.removeEventListener('pointerdown', onPointerDown)
        canvas.removeEventListener('pointermove', onPointerMove)
        canvas.removeEventListener('pointerup', onPointerUpOrCancel)
        canvas.removeEventListener('pointercancel', onPointerUpOrCancel)
      }
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      document.removeEventListener('visibilitychange', onVis)
      canvas.removeEventListener('click', onClickCanvas)
      s.resizeObs?.disconnect()
    }
  }, [])

  return (
    <Layout settingAPI={settingAPI} footerAPI={footerAPI}>
      <Container className="pt-largeDesktop pb-mediumDesktop flex justify-between">
        <ResponsiveTitle left={notFoundListAPI.title.left} right={notFoundListAPI.title.right} />
      </Container>
      <Container className="flex flex-col gap-y-mediumMobile lg:gap-y-mediumDesktop">
        <Spacer />
        <span className="text-center text-bodyMobile md:text-headingCardsDesktop font-ttDrugsTrial text-umber">
          {notFoundListAPI.subtitle}
        </span>
        {!isMobile && (
          <p className="text-center text-body2Desktop text-umber font-chivoMono">
            In the mean time, Let’s play PONG!
            <br />
            Use{' '}
            <span className="relative top-[16px] inline-block">
              <svg
                width="25"
                height="50"
                viewBox="0 0 25 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.884375"
                  y="0.599951"
                  width="23.2"
                  height="23.2"
                  rx="1.2"
                  stroke="#857D66"
                  strokeWidth="0.8"
                />
                <path
                  d="M9.12148 16.7L6.30548 7.73995H7.39348L9.76148 15.292H9.82548L12.1935 7.73995H13.1535L15.5215 15.292H15.5855L17.9535 7.73995H18.6575L15.8415 16.7H14.8815L12.5135 9.21195H12.4495L10.0815 16.7H9.12148Z"
                  fill="#857D66"
                />
                <rect
                  x="0.884375"
                  y="26.2"
                  width="23.2"
                  height="23.2"
                  rx="1.2"
                  stroke="#857D66"
                  strokeWidth="0.8"
                />
                <path
                  d="M12.4832 42.556C10.0512 42.556 8.83523 40.572 8.83523 40.572L9.21923 40.252C9.21923 40.252 10.3072 41.98 12.4832 41.98C13.8912 41.98 14.7872 41.084 14.7872 39.932C14.7872 37.948 9.41123 38.332 9.41123 35.452C9.41123 34.236 10.5632 33.084 12.5472 33.084C14.5312 33.084 15.7472 34.748 15.7472 34.748L15.3632 35.068C15.3632 35.068 14.2112 33.66 12.5472 33.66C11.3312 33.66 10.4992 34.492 10.4992 35.452C10.4992 37.436 15.8752 37.052 15.8752 39.932C15.8752 41.276 14.5952 42.556 12.4832 42.556Z"
                  fill="#857D66"
                />
              </svg>
            </span>{' '}
            to play on the left side
            <br />
            Use{' '}
            <span className="relative top-[16px] inline-block">
              <svg
                width="25"
                height="51"
                viewBox="0 0 25 51"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.884375"
                  y="1.3126"
                  width="23.2"
                  height="23.2"
                  rx="1.2"
                  stroke="#857D66"
                  strokeWidth="0.8"
                />
                <path
                  d="M12.2136 6.9126L12.2136 6.91303L12.0961 6.91303C11.8379 7.6999 11.3984 8.4242 10.8437 9.04157C9.68055 10.3312 8.08043 11.168 6.42383 11.6628L6.75126 12.6838C7.17152 12.5437 7.58572 12.3828 7.98997 12.2013C8.71689 11.873 9.42216 11.4716 10.0606 10.9946C10.1824 10.908 10.3644 10.7569 10.4819 10.6637C10.5512 10.6035 10.7163 10.4572 10.7842 10.3978C10.9353 10.2551 11.1205 10.077 11.2592 9.9223C11.3138 9.86401 11.3887 9.78279 11.4406 9.72365C11.4698 9.68979 11.543 9.60279 11.5713 9.56958C11.7912 9.30193 11.9929 9.01885 12.1727 8.72291L11.9431 18.9126L13.0251 18.9126L12.7955 8.72291C13.0803 9.19071 13.4201 9.62572 13.8005 10.0211C13.9063 10.1351 14.0717 10.2904 14.184 10.3978C14.2504 10.4569 14.4168 10.6018 14.4863 10.6637C14.553 10.7167 14.7365 10.8649 14.8001 10.9151C14.9663 11.0396 15.1721 11.1894 15.345 11.3025C15.7854 11.5982 16.2589 11.8595 16.7365 12.0898C16.8748 12.1545 17.0845 12.2503 17.2219 12.308C17.5483 12.4468 17.8807 12.572 18.2167 12.6841L18.5442 11.663C17.7653 11.4322 17.0079 11.1232 16.2963 10.734C16.0235 10.5806 15.7304 10.4038 15.4749 10.2236C15.3632 10.1496 15.194 10.0189 15.0862 9.93901C15.0217 9.88715 14.8719 9.76114 14.8079 9.71014C14.7495 9.65914 14.5993 9.52264 14.5413 9.46929C14.4599 9.39493 14.366 9.29378 14.2872 9.21621C14.0942 9.01949 13.9122 8.80199 13.7475 8.5817C13.369 8.07534 13.0688 7.51197 12.8717 6.91346L12.7542 6.91346L12.7542 6.91303L12.2132 6.91303L12.2136 6.9126Z"
                  fill="#857D66"
                />
                <rect
                  x="0.884375"
                  y="26.9127"
                  width="23.2"
                  height="23.2"
                  rx="1.2"
                  stroke="#857D66"
                  strokeWidth="0.8"
                />
                <path
                  d="M12.7551 44.5127L12.7551 44.5123L12.8727 44.5123C13.1308 43.7254 13.5704 43.0011 14.125 42.3837C15.2882 41.0941 16.8883 40.2573 18.5449 39.7625L18.2175 38.7415C17.7972 38.8816 17.383 39.0425 16.9788 39.224C16.2519 39.5523 15.5466 39.9537 14.9082 40.4307C14.7864 40.5173 14.6044 40.6683 14.4868 40.7616C14.4176 40.8218 14.2525 40.9681 14.1845 41.0275C14.0335 41.1702 13.8482 41.3483 13.7095 41.503C13.655 41.5613 13.5801 41.6425 13.5282 41.7016C13.4989 41.7355 13.4258 41.8225 13.3974 41.8557C13.1776 42.1234 12.9759 42.4064 12.796 42.7024L13.0257 32.5127L11.9436 32.5127L12.1732 42.7024C11.8884 42.2346 11.5487 41.7996 11.1682 41.4042C11.0624 41.2902 10.8971 41.1348 10.7848 41.0275C10.7183 40.9683 10.5519 40.8235 10.4824 40.7616C10.4158 40.7086 10.2323 40.5603 10.1686 40.5102C10.0024 40.3857 9.79664 40.2359 9.62373 40.1228C9.18334 39.827 8.70984 39.5658 8.23223 39.3355C8.09394 39.2707 7.88425 39.175 7.74683 39.1173C7.42049 38.9785 7.08808 38.8533 6.752 38.7412L6.42457 39.7623C7.20343 39.9931 7.96086 40.3021 8.6724 40.6913C8.94529 40.8447 9.23831 41.0215 9.49389 41.2017C9.60555 41.2756 9.77478 41.4064 9.88255 41.4863C9.94704 41.5381 10.0968 41.6641 10.1609 41.7152C10.2193 41.7662 10.3695 41.9026 10.4275 41.956C10.5088 42.0304 10.6028 42.1315 10.6815 42.2091C10.8746 42.4058 11.0566 42.6233 11.2213 42.8436C11.5998 43.35 11.8999 43.9133 12.0971 44.5118L12.2146 44.5118L12.2146 44.5123L12.7556 44.5123L12.7551 44.5127Z"
                  fill="#857D66"
                />
              </svg>
            </span>{' '}
            to play on the right side
          </p>
        )}
      </Container>
      {!isMobile && (
        <Container
          ref={containerRef}
          className="h-[80svh] max-h-[700px] mt-mediumDesktop"
        >
          <Spacer />
          {/* Mid Line */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 4 601"
            fill="none"
            preserveAspectRatio="none"
            className="absolute top-0 left-1/2 -translate-x-1/2 h-full z-10 pointer-events-none py-[50px]"
          >
            <path
              d="M0.499974 600.971C1.42998 408.769 1.40999 193.168 0.5 0.970947L3.5 0.970947C2.61999 166.177 2.73998 360.573 3.12998 525.971L3.49997 600.971L0.499974 600.971Z"
              fill="#857D66"
            />
          </svg>
          <canvas ref={canvasRef} className="w-full h-full" />
          <Spacer />
        </Container>
      )}
      <RectangleButton
        name="BACK TO HOME"
        link="/"
        className="mt-mediumMobile mb-mediumMobile lg:mt-mediumDesktop lg:mb-largeDesktop mx-auto"
      />
      {isMobile && (
        <Container outerClassName="mb-mediumMobile">
          <Spacer />
        </Container>
      )}
    </Layout>
  )
}

export default NotFoundPage
